import { IconType } from 'react-icons';
import { TbBuildingSkyscraper, TbTool, TbUsers } from 'react-icons/tb';

import { CREDIT_COST, CreditUsageType, CreditUsageTypeCosts } from 'models';

export type PersonaUsage = {
    type: CreditUsageType;
    label: string;
    runsPerMonth: number;
    creditsPerRun: number;
};

export type SupportPreset = {
    quickQuestions: number;
    designInvestigations: number;
    detailedAnalyses: number;
    comprehensiveReviews: number;
};

export type PersonaPreset = {
    id: string;
    title: string;
    subtitle: string;
    description: string;
    icon: IconType;
    simUsage: PersonaUsage[];
    supportPreset: SupportPreset;
    totalMonthlyCredits: number;
};

export type SupportType = {
    id: string;
    label: string;
    description: string;
    whatYouGet: string;
    creditsPerUnit: number;
    unit: string;
    maxPerMonth: number;
};

export const SUPPORT_TYPES: SupportType[] = [
    {
        id: 'quickQuestions',
        label: 'Expert question',
        description: 'A single, focused answer to a technical question',
        whatYouGet: 'A direct answer with a recommendation or explanation, typically within hours.',
        creditsPerUnit: 250,
        unit: 'question',
        maxPerMonth: 20,
    },
    {
        id: 'designInvestigations',
        label: 'Design investigation',
        description: 'We review your project and run simulations',
        whatYouGet: 'A written analysis with simulation results, parameter recommendations, and next steps.',
        creditsPerUnit: 1000,
        unit: 'investigation',
        maxPerMonth: 10,
    },
    {
        id: 'detailedAnalyses',
        label: 'Detailed analysis',
        description: 'Documented results suitable for stakeholder sign-off',
        whatYouGet: 'A documented report with simulation data, tables, and recommendations.',
        creditsPerUnit: 2000,
        unit: 'report',
        maxPerMonth: 5,
    },
    {
        id: 'comprehensiveReviews',
        label: 'Comprehensive review',
        description: 'Full engineering review across multiple aspects of your design',
        whatYouGet:
            'A comprehensive review covering multiple engineering disciplines, with a full report and actionable recommendations.',
        creditsPerUnit: 4000,
        unit: 'review',
        maxPerMonth: 3,
    },
];

export const PERSONA_PRESETS: PersonaPreset[] = [
    {
        id: 'solo',
        title: 'Solo',
        subtitle: 'You design, we back you up',
        description:
            'You design your own DC systems and run simulations to validate your work. You occasionally need a quick answer from an expert.',
        icon: TbTool,
        simUsage: [
            {
                type: CreditUsageType.POWER_FLOW_SIMULATION,
                label: 'Power flow simulations',
                runsPerMonth: 50,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.POWER_FLOW_SIMULATION],
            },
            {
                type: CreditUsageType.WIRE_SIZING_SIMULATION,
                label: 'Wire sizing runs',
                runsPerMonth: 100,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.WIRE_SIZING_SIMULATION],
            },
            {
                type: CreditUsageType.FAULT_SIMULATION,
                label: 'Fault simulations',
                runsPerMonth: 50,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.FAULT_SIMULATION],
            },
            {
                type: CreditUsageType.DIAGRAM_AI_MESSAGE,
                label: 'AI design agent messages',
                runsPerMonth: 10,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.DIAGRAM_AI_MESSAGE],
            },
        ],
        supportPreset: { quickQuestions: 1, designInvestigations: 0, detailedAnalyses: 0, comprehensiveReviews: 0 },
        // 50*4 + 100*1 + 50*4 + 10*2 + 1*250 = 770
        totalMonthlyCredits: 770,
    },
    {
        id: 'co-work',
        title: 'Co-work',
        subtitle: 'We work alongside you',
        description:
            'Your team designs and simulates actively. Our DC experts review your work and help with tricky protection or sizing questions.',
        icon: TbUsers,
        simUsage: [
            {
                type: CreditUsageType.POWER_FLOW_SIMULATION,
                label: 'Power flow simulations',
                runsPerMonth: 50,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.POWER_FLOW_SIMULATION],
            },
            {
                type: CreditUsageType.WIRE_SIZING_SIMULATION,
                label: 'Wire sizing runs',
                runsPerMonth: 100,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.WIRE_SIZING_SIMULATION],
            },
            {
                type: CreditUsageType.FAULT_SIMULATION,
                label: 'Fault simulations',
                runsPerMonth: 50,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.FAULT_SIMULATION],
            },
            {
                type: CreditUsageType.DIAGRAM_AI_MESSAGE,
                label: 'AI design agent messages',
                runsPerMonth: 10,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.DIAGRAM_AI_MESSAGE],
            },
        ],
        supportPreset: { quickQuestions: 2, designInvestigations: 2, detailedAnalyses: 1, comprehensiveReviews: 0 },
        // 50*4 + 100*1 + 50*4 + 10*2 + 2*250 + 2*1000 + 1*2000 = 5220
        totalMonthlyCredits: 5220,
    },
    {
        id: 'partner',
        title: 'Partner',
        subtitle: 'We handle it for you',
        description:
            'Completely unburden your team. Our DC engineers handle topology, protection coordination, and validated bills of materials.',
        icon: TbBuildingSkyscraper,
        simUsage: [
            {
                type: CreditUsageType.POWER_FLOW_SIMULATION,
                label: 'Power flow simulations',
                runsPerMonth: 50,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.POWER_FLOW_SIMULATION],
            },
            {
                type: CreditUsageType.WIRE_SIZING_SIMULATION,
                label: 'Wire sizing runs',
                runsPerMonth: 100,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.WIRE_SIZING_SIMULATION],
            },
            {
                type: CreditUsageType.FAULT_SIMULATION,
                label: 'Fault simulations',
                runsPerMonth: 50,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.FAULT_SIMULATION],
            },
            {
                type: CreditUsageType.DIAGRAM_AI_MESSAGE,
                label: 'AI design agent messages',
                runsPerMonth: 10,
                creditsPerRun: CreditUsageTypeCosts[CreditUsageType.DIAGRAM_AI_MESSAGE],
            },
        ],
        supportPreset: { quickQuestions: 0, designInvestigations: 2, detailedAnalyses: 2, comprehensiveReviews: 2 },
        // 50*4 + 100*1 + 50*4 + 10*2 + 2*1000 + 2*2000 + 2*4000 = 14620
        totalMonthlyCredits: 14620,
    },
];
